import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
  startTransition,
} from 'react';

import { Flex, Skeleton, rem } from '@vimeo/bokeh';
import { BokehOnly } from '@vimeo/core/components/BokehOnly';
import { IrisOnly } from '@vimeo/core/components/IrisOnly';
import { useOrionSettings } from '@vimeo/orion';
import { shouldShowInDevelopmentFeature } from '@vimeo/query-feature-flag';

import { InputWrapper, SearchToggle } from './components';
import { OmnisearchContext } from './containers/OmnisearchContext';
import { useSearchCapabilities } from './hooks/useSearchCapabilities';
import { EXPANDED_SEARCH_WIDTH, PADDING_FOR_MODE_SWITCHER } from './styles';
import { PropsWithConflictingElements } from './types';

const Search = React.lazy(() => import('./SearchField'));
const SearchInput = React.lazy(() => import('./components/SearchInput'));

interface Props extends PropsWithConflictingElements {
  withToggle?: boolean;
  updateGlobalNavSearchState?: (isOpen: boolean) => void;
  leftNavbarRef?: React.RefObject<HTMLDivElement | null>;
  fadeOutLeftNav?: boolean;
  showRefreshedLihpChanges?: boolean;
  'data-testid'?: string;
  /** To be used when `withToggle` is true */
  inputStyle?: React.CSSProperties;
}

export function SearchField(props: Props) {
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [changeEvent, setChangeEvent] = useState<React.ChangeEvent<HTMLInputElement>>();
  const { hasOmnisearch, hasPaid, ...capabilities } = useSearchCapabilities();
  const { withToggle, updateGlobalNavSearchState, ...omnisearchProps } = props;
  const { folderName } = useContext(OmnisearchContext);
  const { settings } = useOrionSettings();
  const hasNewOmnisearch = shouldShowInDevelopmentFeature('has_new_omnisearch', true);
  const enableFolderScopedSearchRequestFix = settings.enable_omnisearch_folder_scoped_request_fix;

  // make sure the placeholder of the fallback is in the right position
  const inputPadding = capabilities.hasSearchTypesDropdown ? PADDING_FOR_MODE_SWITCHER : 0;

  useEffect(() => {
    const getSearchStartPoint = () => {
      const searchInputField = document.getElementById('search-wrapper');
      const searchRect = searchInputField?.getClientRects();
      if (searchRect && searchRect.length > 0) {
        return searchRect[0].left;
      }
      return 0;
    };

    // Blur/Hide leftnav if search input is open and overlaps with leftnav
    const blurMenu = () => {
      const leftNav = props.leftNavbarRef?.current;
      if (leftNav && showSearchInput) {
        const leftNavRect = leftNav.getClientRects();
        if (leftNavRect.length > 0) {
          const leftNavEndPoint = leftNavRect[0].right;
          const searchStartPoint = getSearchStartPoint();

          if (searchStartPoint > 0 && leftNavEndPoint > searchStartPoint - EXPANDED_SEARCH_WIDTH)
            leftNav.style.cssText = props.fadeOutLeftNav
              ? `transition: opacity 250ms ease; opacity: 0;`
              : 'filter: blur(1.5px);';
        }
      } else if (leftNav) leftNav.removeAttribute('style');
    };
    blurMenu();
  }, [
    props.leftNavbarRef,
    showSearchInput,
    capabilities.hasSearchTypesDropdown,
    props.fadeOutLeftNav,
  ]);

  useEffect(() => {
    updateGlobalNavSearchState?.(showSearchInput);
  }, [showSearchInput, updateGlobalNavSearchState]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const modifierKey = /mac/i.test(navigator.platform) ? event.metaKey : event.ctrlKey;
      if (modifierKey && event.key === 'k') {
        event.preventDefault();
        if (withToggle) setShowSearchInput((showInput) => !showInput);
      }
    },
    [withToggle],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!hasOmnisearch) return null;
  if (hasNewOmnisearch) return <></>;

  return withToggle ? (
    <Flex
      justifyContent="flex-end"
      marginLeft={props.showRefreshedLihpChanges ? rem(14) : 'unset'}
      grow={showSearchInput ? 1 : 'unset'}
    >
      {showSearchInput ? (
        <InputWrapper id="search-wrapper" sx={props.inputStyle}>
          <Suspense
            fallback={
              <SearchPlaceholder>
                <SearchInput paddingLeft={inputPadding} onChange={setChangeEvent} isFallBack />
              </SearchPlaceholder>
            }
          >
            <Search
              {...omnisearchProps}
              {...capabilities}
              enableFolderScopedSearchRequestFix={enableFolderScopedSearchRequestFix}
              setShowSearchInput={() => startTransition(() => setShowSearchInput(false))}
              autoFocus
              data-testid={props['data-testid']}
              folderName={folderName}
            />
          </Suspense>
        </InputWrapper>
      ) : (
        <SearchToggle
          data-testid={props['data-testid'] ? `${props['data-testid']}-toggle` : undefined}
          onClick={() => startTransition(() => setShowSearchInput(true))}
        />
      )}
    </Flex>
  ) : (
    <Suspense
      fallback={
        <SearchPlaceholder>
          <SearchInput paddingLeft={inputPadding} onChange={setChangeEvent} isFallBack />
        </SearchPlaceholder>
      }
    >
      <Search
        {...omnisearchProps}
        {...capabilities}
        enableFolderScopedSearchRequestFix={enableFolderScopedSearchRequestFix}
        data-testid={props['data-testid']}
        folderName={folderName}
        changeEvent={changeEvent}
        width={props.width}
        maxWidth={props.maxWidth}
      />
    </Suspense>
  );
}

const SearchPlaceholder = ({ children }: React.PropsWithChildren) => {
  return (
    <>
      <BokehOnly>
        <Skeleton maxW="500px" w="100%" h="40px" borderRadius="12px" />
      </BokehOnly>
      <IrisOnly>{children}</IrisOnly>
    </>
  );
};

export { useSearchCapabilities };
